import { useMemo, useRef, useState } from 'react';
import { parseMenuText } from '../../lib/menuImport';
import { importMenuFromText } from '../../lib/menuImportService';
import { extractTextFromImageFile, extractTextFromScannedPdf } from '../../lib/ocr';
import { compressImageForVision, extractMenuFromImageAI, pdfToImages } from '../../lib/menuVision';
import { formatPrice } from '../../lib/cart';

/**
 * Panneau d'import de menu (back-office) — UNE SEULE méthode fiable.
 *
 * Le resto clique « Importer ma carte », choisit une ou plusieurs PHOTOS **ou** un PDF,
 * et l'IA vision (Edge `extract-menu-vision`) lit le menu AVEC les descriptions, au
 * format « Nom | PRIX € | Description ». Un PDF est d'abord rendu en images (pdf.js)
 * puis lu par la MÊME IA — avant, un PDF partait en OCR et perdait les descriptions.
 * L'OCR Tesseract ne sert plus que de filet silencieux de dernier recours.
 *
 * Le texte lu tombe dans une zone éditable + un aperçu (avec la description visible),
 * que le resto VÉRIFIE avant d'importer. Logique pure : menuImport.ts. Pose en base :
 * menuImportService.ts (RPC sécurisée `import_menu`, réservée au propriétaire).
 *
 * (L'ancien import « lien Uber Eats » a été retiré : Uber bloquait le fetch serveur.)
 */
export default function MenuImportPanel({
  tenantId,
  currency,
  onImported,
}: {
  tenantId: string;
  currency: 'EUR' | 'CHF';
  onImported: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false); // import en base
  const [readBusy, setReadBusy] = useState(false); // lecture des fichiers
  const [readPct, setReadPct] = useState(0);
  const [msg, setMsg] = useState<string | null>(null);
  const [provider, setProvider] = useState<string | null>(null); // dernier fournisseur IA effectif
  const fileRef = useRef<HTMLInputElement>(null);

  // Aperçu recalculé en direct à chaque frappe (pur, instantané).
  const preview = useMemo(() => parseMenuText(text), [text]);

  /**
   * Lit UN fichier avec l'IA vision d'abord, OCR Tesseract en secours.
   * - Image : envoyée directement à l'IA.
   * - PDF   : rendu page par page en images (pdf.js) puis lu par l'IA — chaque page
   *           lue est concaténée. OCR seulement si l'IA échoue complètement.
   */
  const readOneFile = async (
    file: File,
  ): Promise<{ text: string; via: 'ia' | 'ocr' | 'none'; provider?: string }> => {
    const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');

    if (isPdf) {
      // 1) PDF → images → IA (la voie fiable pour les descriptions).
      try {
        const buf = await file.arrayBuffer();
        const images = await pdfToImages(buf);
        const parts: string[] = [];
        let prov: string | undefined;
        for (const img of images) {
          try {
            const { text: aiText, provider: p } = await extractMenuFromImageAI(img.data, img.mediaType);
            if (aiText.trim()) { parts.push(aiText.trim()); prov = prov ?? p; }
          } catch {
            // page illisible par l'IA → on continue les autres pages
          }
        }
        if (parts.length) return { text: parts.join('\n'), via: 'ia', provider: prov };
      } catch {
        // rendu PDF impossible → OCR de secours ci-dessous
      }
      // 2) Secours : OCR du PDF scanné.
      try {
        const ocrText = await extractTextFromScannedPdf(await file.arrayBuffer(), setReadPct);
        if (ocrText.trim()) return { text: ocrText.trim(), via: 'ocr' };
      } catch {
        /* ignore */
      }
      return { text: '', via: 'none' };
    }

    // Image : IA d'abord, sur une version HD recompressée (< 4 Mo, limite Groq).
    try {
      const { data, mediaType } = await compressImageForVision(file);
      const { text: aiText, provider: p } = await extractMenuFromImageAI(data, mediaType);
      if (aiText.trim()) return { text: aiText.trim(), via: 'ia', provider: p };
    } catch {
      // IA indispo (clé/quota) → repli OCR ci-dessous.
    }
    try {
      const ocrText = await extractTextFromImageFile(file, setReadPct);
      if (ocrText.trim()) return { text: ocrText.trim(), via: 'ocr' };
    } catch {
      /* ignore */
    }
    return { text: '', via: 'none' };
  };

  /**
   * Lecture d'UNE OU PLUSIEURS photos/PDF (carte sur 2-3 photos, ou PDF multi-pages).
   * Les textes lus sont FUSIONNÉS et AJOUTÉS à la zone (on peut charger en plusieurs fois).
   * Toujours relire l'aperçu avant d'importer.
   */
  const onFiles = async (files: FileList | null | undefined) => {
    const list = files ? Array.from(files) : [];
    if (list.length === 0) return;
    setReadBusy(true);
    setReadPct(0);
    setProvider(null);
    const parts: string[] = [];
    let aiCount = 0;
    let ocrCount = 0;
    let lastProvider: string | undefined;
    try {
      for (let i = 0; i < list.length; i++) {
        setMsg(`🤖 Lecture ${i + 1}/${list.length}… (IA, quelques secondes par fichier)`);
        const { text: t, via, provider: p } = await readOneFile(list[i]);
        if (t) {
          parts.push(t);
          if (via === 'ia') { aiCount += 1; if (p) lastProvider = p; }
          else if (via === 'ocr') ocrCount += 1;
        }
      }
      if (parts.length === 0) {
        setProvider('error');
        setMsg('⚠️ Rien lu sur ces fichiers. Réessayez avec des photos nettes / un PDF lisible, ou tapez le menu ci-dessous.');
        return;
      }
      setProvider(ocrCount && !aiCount ? 'ocr' : lastProvider ?? null);
      const combined = parts.join('\n');
      setText((prev) => (prev.trim() ? prev.trim() + '\n' + combined : combined));
      const detail = `${aiCount} par IA${ocrCount ? `, ${ocrCount} par OCR de secours` : ''}`;
      setMsg(`✅ ${list.length} fichier(s) lu(s) (${detail}) — ⚠️ vérifiez l’aperçu (prix, descriptions, doublons) puis importez.`);
    } catch {
      setProvider('error');
      setMsg('⚠️ Lecture impossible. Réessayez, ou tapez/collez le menu ci-dessous.');
    } finally {
      setReadBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  /** Badge du fournisseur IA effectif : vert Groq, orange Gemini (secours), rouge erreur. */
  const providerBadge = (p: string): { label: string; className: string } => {
    if (p === 'groq') return { label: '🟢 Groq Vision', className: 'bg-green-100 text-green-700' };
    if (p === 'gemini') return { label: '🟠 Gemini (secours)', className: 'bg-amber-100 text-amber-700' };
    if (p === 'openai') return { label: '🔵 OpenAI (repli)', className: 'bg-sky-100 text-sky-700' };
    if (p === 'anthropic') return { label: '🔵 Claude (repli)', className: 'bg-sky-100 text-sky-700' };
    if (p === 'ocr') return { label: '⚪ OCR de secours', className: 'bg-slate-100 text-slate-600' };
    if (p === 'error') return { label: '🔴 Aucune lecture', className: 'bg-red-100 text-red-700' };
    return { label: `IA (${p})`, className: 'bg-slate-100 text-slate-600' };
  };

  const doImport = async () => {
    setBusy(true);
    setMsg('⏳ Import en cours…');
    try {
      const res = await importMenuFromText(tenantId, text);
      if (res.ok) {
        setMsg(`✅ ${res.count} produits importés — l’ancien menu a été remplacé.`);
        setText('');
        onImported();
      } else {
        // Raison explicite (droits, auth, schéma…) : ne JAMAIS l'avaler silencieusement.
        setMsg(`⚠️ ${res.error ?? 'Import refusé (raison inconnue).'}`);
      }
    } catch (e) {
      // Exception réseau/inattendue : avant, le bouton se réactivait sans rien afficher
      // (« rien ne se passe »). Désormais on montre toujours le message.
      setMsg(`❌ Erreur pendant l’import : ${(e as Error)?.message ?? String(e)}`);
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="mb-4 w-full rounded-lg border border-dashed border-brand-primary py-2.5 text-sm font-medium text-brand-primary hover:bg-brand-light"
      >
        📋 Importer un menu
      </button>
    );
  }

  return (
    <div className="mb-5 rounded-xl border border-slate-200 p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-bold text-sm">Importer un menu</h3>
        <button onClick={() => setOpen(false)} className="text-slate-400 text-sm">Fermer ✕</button>
      </div>

      {/* UNE seule voie : photo(s) ou PDF → IA vision (descriptions incluses). */}
      <p className="text-xs opacity-70 mb-2">
        Envoyez une ou <strong>plusieurs photos nettes</strong> de votre carte, ou son <strong>PDF</strong>.
        L’<strong>IA</strong> lit le menu <strong>avec les descriptions</strong>. Un PDF est lu comme une image
        (pas d’OCR). <strong>Vérifiez toujours l’aperçu</strong> (prix, descriptions, doublons) avant d’importer.
      </p>

      <input
        ref={fileRef}
        type="file"
        accept="image/*,application/pdf,.pdf"
        multiple
        onChange={(e) => onFiles(e.target.files)}
        className="hidden"
      />
      <button
        type="button"
        disabled={readBusy}
        onClick={() => fileRef.current?.click()}
        className="rounded-lg py-2 px-4 text-sm font-semibold text-white disabled:opacity-50"
        style={{ background: 'var(--brand-primary)' }}
      >
        {readBusy ? `Lecture… ${readPct > 0 ? Math.round(readPct * 100) + '%' : ''}` : '📷 Importer ma carte (photo ou PDF)'}
      </button>

      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={6}
        placeholder={'Le texte lu apparaît ici (modifiable).\nFormat : « Nom | 6,50 € | Description ».\n### Sandwichs\nSandwich kebab | 7,00 € | Viande kebab, frites, crudités\n### Boissons\nCoca-Cola | 2,50 € |'}
        className="mt-3 w-full border border-slate-200 rounded-lg p-2 text-sm font-mono"
      />

      {preview.totals.products > 0 && (
        <div className="mt-3 rounded-lg bg-slate-50 p-3 max-h-72 overflow-y-auto">
          <p className="text-xs font-semibold mb-2 opacity-70">
            Aperçu : {preview.totals.categories} catégories · {preview.totals.products} produits
          </p>
          {preview.categories.map((c) => (
            <div key={c.name} className="mb-2">
              <div className="text-xs font-bold">{c.emoji} {c.name}</div>
              <ul className="ml-3">
                {c.products.map((p, i) => (
                  <li key={i} className="py-0.5">
                    <div className="flex justify-between text-xs">
                      <span className="truncate">{p.name}</span>
                      <span className="font-semibold flex-shrink-0 ml-2">{formatPrice(p.priceCents, currency)}</span>
                    </div>
                    {p.description ? (
                      <div className="text-[11px] text-slate-500 leading-tight">{p.description}</div>
                    ) : (
                      <div className="text-[11px] text-amber-600 leading-tight">⚠️ sans description</div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      {(msg || provider) && (
        <div className="flex items-center flex-wrap gap-2 mt-2">
          {provider && (
            <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${providerBadge(provider).className}`}>
              {providerBadge(provider).label}
            </span>
          )}
          {msg && <p className="text-sm">{msg}</p>}
        </div>
      )}

      <div className="flex items-center gap-2 mt-3">
        <button
          disabled={busy || preview.totals.products === 0}
          onClick={doImport}
          className="rounded-lg py-2 px-4 text-sm font-semibold text-white disabled:opacity-40"
          style={{ background: 'var(--brand-primary)' }}
        >
          {busy ? 'Import en cours…' : `Importer ${preview.totals.products || ''} produits`}
        </button>
        <span className="text-xs text-red-600">⚠️ Remplace le menu actuel</span>
      </div>
    </div>
  );
}
